import { useState } from 'react';
import Text from 'common/ui/Text';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { TabProps } from '@mui/material/Tab';
import { Stack } from 'common/ui/Layout';
import { Colors } from 'styles';
import { TabContext, TabPanel } from '@mui/lab';
import { ReactComponent as ReporterSettingsIcon } from 'assets/reporterIconNav.svg';
import { ReactComponent as ViewerSettingsIcon } from 'assets/ViewerSettingsIcon.svg';
import WorklistSettingsIcon from '@mui/icons-material/List';
import { ReactComponent as PersonalInformationIcon } from 'assets/PersonalInformationIcon.svg';
import SecuritySettingsIcon from '@mui/icons-material/Security';
import { transparentize } from 'color2k';
import { ViewerSettingsTab } from './ViewerSettingsTab';
import { PersonalInformationTab } from './PersonalInformationTab';
import { SecuritySettingsTab } from './SecuritySettingsTab';
import { ReporterSettingsTab } from './ReporterSettingsTab';
import { WorklistSettingsTab } from './WorklistSettingsTab';
import GeneralSettingsIcon from '@mui/icons-material/Tune';
import SvgIcon from '@mui/material/SvgIcon';
import { GeneralSettingsTab } from './GeneralSettingsTab';
import { styled } from '@mui/material/styles';

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(() => ({
  width: 228,
  minHeight: 'auto',
  padding: '8px 4px 8px 16px',
  fontWeight: 400,
  height: 48,
  textTransform: 'none',
  color: Colors.gray10,
  justifyContent: 'flex-start',
  flexDirection: 'row',

  '& .MuiTab-icon': {
    marginBottom: 0,
    marginRight: 32,
    width: 24,
  },

  '&.Mui-selected': {
    backgroundColor: transparentize(Colors.blue2, 0.92),
  },
}));

const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
}));

const StyledTabPanel = styled(TabPanel)(() => ({
  padding: 0,
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
}));

type AccountProps = {
  onClose: () => void;
};

function Account({ onClose }: AccountProps): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState('personal-information');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <TabContext value={value}>
      <Stack style={{ height: '760px', maxHeight: '760px', overflow: 'hidden', width: '948px' }}>
        <div style={{ width: '228px', backgroundColor: Colors.gray2 }}>
          <Text
            variant="display2"
            style={{
              margin: '1rem 16px',
              display: 'inline-block',
              minHeight: '48px',
              lineHeight: '48px',
            }}
          >
            Settings
          </Text>

          <StyledTabs value={value} onChange={handleTabChange} orientation="vertical">
            <StyledTab
              icon={<PersonalInformationIcon />}
              label="Personal Information"
              value="personal-information"
              data-testid="personal-information"
            />
            <StyledTab
              icon={<ViewerSettingsIcon />}
              label="Viewer Settings"
              value="viewer-settings"
              data-testid="viewer-settings"
            />
            <StyledTab
              icon={<SvgIcon component={ReporterSettingsIcon} />}
              label="Reporter Settings"
              value="reporter-settings"
              data-testid="reporter-settings-tab"
            />
            <StyledTab
              icon={<WorklistSettingsIcon />}
              label="Worklist Settings"
              value="worklist-settings"
              data-testid="worklist-settings-tab"
            />
            <StyledTab
              icon={<SecuritySettingsIcon />}
              label="Security Settings"
              value="security-settings"
              data-testid="security-settings-tab"
            />
            <StyledTab
              icon={<GeneralSettingsIcon />}
              label="General Settings"
              value="general-settings"
              data-testid="general-settings-tab"
            />
          </StyledTabs>
        </div>

        <StyledTabPanel value="personal-information">
          <PersonalInformationTab error={error} onClose={onClose} setError={setError} />
        </StyledTabPanel>
        <StyledTabPanel value="viewer-settings">
          <ViewerSettingsTab onClose={onClose} setError={setError} />
        </StyledTabPanel>
        <StyledTabPanel value="security-settings">
          <SecuritySettingsTab onClose={onClose} />
        </StyledTabPanel>
        <StyledTabPanel value="reporter-settings">
          <ReporterSettingsTab onClose={onClose} setError={setError} />
        </StyledTabPanel>
        <StyledTabPanel value="worklist-settings">
          <WorklistSettingsTab onClose={onClose} setError={setError} />
        </StyledTabPanel>
        <StyledTabPanel value="general-settings">
          <GeneralSettingsTab onClose={onClose} setError={setError} />
        </StyledTabPanel>
      </Stack>
    </TabContext>
  );
}

export default Account;
